import { useState, useEffect, useRef } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Send, Sparkles, RefreshCw, CheckCircle2, Brain } from "lucide-react";
import { toast } from "sonner";

/**
 * Conversational Campaign Wizard — Brand Pro · Phase C.
 *
 * Multi-turn chat where Claude both responds AND emits the structured
 * MatcherRunIn state as it's pieced together. When the brand confirms the
 * brief, parent runs the matcher with the extracted payload.
 */
export default function MatcherWizard({ onRunReady }) {
  const [messages, setMessages] = useState([]);   // [{role,content}]
  const [input, setInput] = useState("");
  const [state, setState] = useState(null);
  const [completeness, setCompleteness] = useState(0);
  const [ready, setReady] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const scrollRef = useRef(null);

  // First-load opener — no user message yet, server returns the kickoff line.
  useEffect(() => {
    if (bootstrapped) return;
    setBootstrapped(true);
    setThinking(true);
    api.post("/brand/matcher/wizard", { messages: [] })
      .then(({ data }) => {
        setMessages([{ role: "assistant", content: data.reply }]);
        setState(data.state); setCompleteness(data.completeness || 0);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wizard failed to start"))
      .finally(() => setThinking(false));
  }, [bootstrapped]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next); setInput(""); setThinking(true);
    try {
      const { data } = await api.post("/brand/matcher/wizard", { messages: next });
      setMessages([...next, { role: "assistant", content: data.reply }]);
      setState(data.state); setReady(!!data.ready);
      setCompleteness(data.completeness || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wizard error");
    } finally { setThinking(false); }
  };

  const runMatcher = () => {
    if (!state || !state.brief || state.brief.length < 20) {
      return toast.error("Brief still too short — keep chatting for a few more turns.");
    }
    onRunReady({
      brief: state.brief, budget_range: state.budget_range || "",
      sports: state.sports || [], regions: state.regions || [],
      audience_size_min: state.audience_size_min ?? null,
      deliverables: state.deliverables || [], timeline: state.timeline || "",
      campaign_goal: state.campaign_goal || "", top_n: 10,
    });
  };

  return (
    <div className="grid md:grid-cols-5 gap-6" data-testid="matcher-wizard">
      {/* LEFT: Chat */}
      <div className="md:col-span-3 surface flex flex-col h-[640px]">
        <div className="px-5 pt-4 pb-3 border-b border-line flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-volt" />
          <div className="label-eyebrow text-volt">Campaign Architect · AI</div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
              data-testid="wizard-thread">
          {messages.map((m, i) => (
            <ChatBubble key={i} role={m.role} text={m.content} />
          ))}
          {thinking && <ChatBubble role="assistant" text="…" />}
        </div>
        <div className="border-t border-line p-3 flex gap-2 items-end">
          <textarea
            rows={2} value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Tell the AI about your campaign…"
            data-testid="wizard-input"
            className="flex-1 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm resize-none" />
          <button onClick={send} disabled={thinking || !input.trim()}
            data-testid="wizard-send"
            className="btn-volt inline-flex items-center gap-2 disabled:opacity-40 h-[50px] px-4">
            {thinking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* RIGHT: Live state preview */}
      <div className="md:col-span-2 surface p-5 flex flex-col h-[640px]" data-testid="wizard-state">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Brief in progress</div>
          <div className="text-[10px] uppercase tracking-widest text-volt font-mono">
            {completeness}% complete
          </div>
        </div>
        <div className="h-1 bg-line mb-5">
          <div className="h-1 bg-volt transition-all" style={{ width: `${completeness}%` }} />
        </div>
        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          <Row label="Brief" value={state?.brief} multi />
          <Row label="Budget" value={state?.budget_range} />
          <Row label="Sports" value={(state?.sports || []).join(" · ")} />
          <Row label="Regions" value={(state?.regions || []).join(" · ")} />
          <Row label="Min audience" value={state?.audience_size_min ? state.audience_size_min.toLocaleString() : ""} />
          <Row label="Deliverables" value={(state?.deliverables || []).join(" · ")} />
          <Row label="Timeline" value={state?.timeline} />
          <Row label="Goal" value={state?.campaign_goal} />
        </div>
        <button onClick={runMatcher}
          disabled={!state || (state.brief || "").length < 20}
          className="btn-volt w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="wizard-run-btn">
          {ready ? <CheckCircle2 className="w-4 h-4" /> : <Brain className="w-4 h-4" />}
          {ready ? "Brief looks great — find athletes" : "Find athletes from this brief"}
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] text-sm leading-relaxed px-4 py-2.5 ${
          isUser ? "bg-volt/10 border border-volt/40 text-white"
                 : "border border-line text-ink"}`}
           data-testid={`wizard-msg-${role}`}>
        {text}
      </div>
    </div>
  );
}

function Row({ label, value, multi }) {
  return (
    <div>
      <div className="label-eyebrow text-[9px] mb-1">{label}</div>
      <div className={`text-sm ${value ? "text-white" : "text-ink-muted"} ${multi ? "leading-relaxed" : "truncate"}`}>
        {value || "—"}
      </div>
    </div>
  );
}
